import React, { createContext, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
    Button,
    Radio,
    Table as RsuiteTable,
    Text,
    Whisper,
    Tooltip,
    IconButton,
} from "rsuite";

import GearIcon from "@rsuite/icons/Gear";
import HelpOutlineIcon from "@rsuite/icons/HelpOutline";

import {
    ITenantFeatureSettingWorkFlow,
    WorkflowModalType,
    ToggleValueType,
} from "utils/tenant-workflows/tenant-workflows-types";
import { axImportNameToWorkflowTypeMapping } from "utils/tenant-workflows/tenant-workflows-definitions";
import {
    getTenantFeatureSettingByType,
    isTenantFeatureSettingWithTypeEnabled,
    isToggleValueEnabled,
    isEveryWorkflowStepCompleted,
    isOn,
    isOff,
    isSupported,
    toState,
} from "utils/tenant-workflows/tenant-workflows-helper";

import {
    DataImport,
    FeatureState,
} from "store/environments/tenants/data-import/data-import-types";

import ProcessModalButton from "components/WorkflowLog/ProcessModalButton/ProcessModalButton";
import Settings from "./Settings/Settings";

import "./Table.less";

export const HistoryContext = createContext<DataImport | null>(null);

interface Props {
    disabled?: boolean;
    isEnroll?: boolean;
    entities: DataImport[];
    originEntities: DataImport[];
    isD365Customer: boolean;
    disabledSettingNames: string[];
    workflows: ITenantFeatureSettingWorkFlow[];
    onEntityChanged: (entity: DataImport) => void;
    onMarkAndTransferAll: ((entity: DataImport) => void) | null;
}

const multiStateEntities = ["PaymentInformation"];

const Table: React.FC<Props> = ({
    disabled = false,
    isEnroll = false,
    entities,
    originEntities,
    isD365Customer,
    disabledSettingNames,
    workflows,
    onEntityChanged,
    onMarkAndTransferAll,
}: Props) => {
    const { t } = useTranslation();

    const headerHeight = 46;
    const rowHeight = 50;

    const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

    const [selectedDataImportItem, setSelectedDataImportItem] =
        useState<DataImport | null>(null);

    const getEntityWorkflow = (
        entity: DataImport
    ): ITenantFeatureSettingWorkFlow | null => {
        if (!isSupported(entity.cef)) {
            return null;
        }

        const workflowType =
            axImportNameToWorkflowTypeMapping[entity.entity] || null;

        return workflowType !== null
            ? getTenantFeatureSettingByType(workflows, workflowType)
            : null;
    };

    const isDocImportOff = (entity: DataImport): boolean => {
        const workflow = getEntityWorkflow(entity);
        if (
            workflow !== null &&
            !isEveryWorkflowStepCompleted(workflow.steps)
        ) {
            return (
                !isToggleValueEnabled(
                    workflow.toggleValue,
                    ToggleValueType.Setting
                ) &&
                !isToggleValueEnabled(
                    workflow.toggleValue,
                    ToggleValueType.AxImport
                )
            );
        }

        return !isOn(entity.axImport) && !isOn(entity.cef);
    };

    const isCefOn = (entity: DataImport): boolean => {
        const workflow = getEntityWorkflow(entity);
        if (
            workflow !== null &&
            !isEveryWorkflowStepCompleted(workflow.steps)
        ) {
            return isToggleValueEnabled(
                workflow.toggleValue,
                ToggleValueType.Setting
            );
        }

        return isOn(entity.cef);
    };

    const isAxImportOn = (entity: DataImport): boolean => {
        const workflow = getEntityWorkflow(entity);
        if (
            workflow !== null &&
            !isEveryWorkflowStepCompleted(workflow.steps)
        ) {
            return isToggleValueEnabled(
                workflow.toggleValue,
                ToggleValueType.AxImport
            );
        }

        return isOn(entity.axImport);
    };

    const isMultiState = (entity: DataImport): boolean => {
        return multiStateEntities.includes(entity.entity);
    };

    const isEntityEnabled = (entity: DataImport): boolean => {
        const workflow = getEntityWorkflow(entity);

        return (
            (workflow === null ||
                (!disabledSettingNames.includes(workflow.featureName) &&
                    isTenantFeatureSettingWithTypeEnabled(
                        workflows,
                        workflow.type
                    ))) &&
            !disabled
        );
    };

    const isCefEnabled = (entity: DataImport): boolean =>
        isSupported(entity.cef) &&
        originEntities.some((e) => e.entity === entity.entity && isOn(e.cef)) &&
        isEntityEnabled(entity);

    const handleAxImportCheck = (value: boolean | null, entity: DataImport) => {
        const multiState = isMultiState(entity);

        if (isSupported(entity.axImport)) {
            entity.axImport = multiState ? toState(value) : FeatureState.On;
        }

        if (isSupported(entity.cef) && !multiState) {
            entity.cef = FeatureState.Off;
        }

        onEntityChanged(entity);
    };

    const handleCefCheck = (value: boolean | null, entity: DataImport) => {
        const multiState = isMultiState(entity);

        if (isSupported(entity.cef)) {
            entity.cef = multiState ? toState(value) : FeatureState.On;
        }

        if (isSupported(entity.axImport) && !multiState) {
            entity.axImport = FeatureState.Off;
        }

        onEntityChanged(entity);
    };
    const handleDisableCheck = (value: boolean | null, entity: DataImport) => {
        if (isSupported(entity.cef)) {
            entity.cef = toState(!value);
        }

        if (isSupported(entity.axImport)) {
            entity.axImport = toState(!value);
        }

        onEntityChanged(entity);
    };

    const handleOpenSettings = (item: DataImport) => {
        setSelectedDataImportItem(item);
        setSettingsOpen(true);
    };

    return (
        <>
            {entities.length > 0 && (
                <>
                    <HistoryContext.Provider value={selectedDataImportItem}>
                        <Settings
                            open={settingsOpen}
                            setOpen={setSettingsOpen}
                        />
                    </HistoryContext.Provider>

                    <RsuiteTable
                        style={{ margin: "0.5rem 0" }}
                        autoHeight={true}
                        shouldUpdateScroll={true}
                        data={entities}
                        headerHeight={headerHeight}
                        rowHeight={rowHeight}
                    >
                        <RsuiteTable.Column flexGrow={3} minWidth={300} fixed>
                            <RsuiteTable.HeaderCell>
                                <Text>
                                    {t(
                                        "tenantConfig.axImportControl.tableHeader.name"
                                    )}
                                </Text>
                            </RsuiteTable.HeaderCell>
                            <RsuiteTable.Cell
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {(rowData: DataImport) => {
                                    const workflow = getEntityWorkflow(rowData);

                                    return (
                                        <>
                                            <Text>
                                                {rowData.entity}
                                                {isD365Customer &&
                                                    isMultiState(rowData) && (
                                                        <Whisper
                                                            placement="topStart"
                                                            trigger="hover"
                                                            speaker={
                                                                <Tooltip>
                                                                    {t(
                                                                        "tenantConfig.axImportControl.dataTransfer.multiSelectionHelpText"
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <HelpOutlineIcon
                                                                style={{
                                                                    marginLeft:
                                                                        "0.25rem",
                                                                }}
                                                            />
                                                        </Whisper>
                                                    )}
                                            </Text>

                                            {workflow && (
                                                <ProcessModalButton
                                                    workflow={workflow}
                                                    workflowType={
                                                        WorkflowModalType.Feature
                                                    }
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </RsuiteTable.Cell>
                        </RsuiteTable.Column>

                        <RsuiteTable.Column flexGrow={1} minWidth={100} fixed>
                            <RsuiteTable.HeaderCell
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Text>
                                    {t(
                                        "tenantConfig.axImportControl.tableHeader.disabled"
                                    )}
                                </Text>
                            </RsuiteTable.HeaderCell>
                            <RsuiteTable.Cell
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                dataKey={"name"}
                            >
                                {(rowData: DataImport) => (
                                    <>
                                        <Radio
                                            checked={isDocImportOff(rowData)}
                                            disabled={!isEntityEnabled(rowData)}
                                            className="ax-import-entities-table__row-checker"
                                            onClick={() => {
                                                handleDisableCheck(true, {
                                                    ...rowData,
                                                });
                                            }}
                                        />
                                    </>
                                )}
                            </RsuiteTable.Cell>
                        </RsuiteTable.Column>

                        <RsuiteTable.Column flexGrow={1} minWidth={100} fixed>
                            <RsuiteTable.HeaderCell
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Text>
                                    {t(
                                        "tenantConfig.axImportControl.tableHeader.axImport"
                                    )}
                                </Text>
                            </RsuiteTable.HeaderCell>
                            <RsuiteTable.Cell
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                dataKey={"name"}
                            >
                                {(rowData: DataImport) =>
                                    isSupported(rowData.axImport) && (
                                        <>
                                            <Radio
                                                className="ax-import-entities-table__row-checker"
                                                checked={isAxImportOn(rowData)}
                                                disabled={
                                                    !isEntityEnabled(rowData)
                                                }
                                                onClick={() => {
                                                    handleAxImportCheck(
                                                        isOff(rowData.axImport),
                                                        {
                                                            ...rowData,
                                                        }
                                                    );
                                                }}
                                            />
                                        </>
                                    )
                                }
                            </RsuiteTable.Cell>
                        </RsuiteTable.Column>

                        {isD365Customer && (
                            <>
                                <RsuiteTable.Column
                                    flexGrow={1}
                                    minWidth={100}
                                    fixed
                                >
                                    <RsuiteTable.HeaderCell
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Text>
                                            {t(
                                                "tenantConfig.axImportControl.tableHeader.cef"
                                            )}
                                        </Text>
                                    </RsuiteTable.HeaderCell>
                                    <RsuiteTable.Cell
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                        dataKey={"name"}
                                    >
                                        {(rowData: DataImport) =>
                                            isSupported(rowData.cef) && (
                                                <>
                                                    <Radio
                                                        checked={isCefOn(
                                                            rowData
                                                        )}
                                                        className="ax-import-entities-table__row-checker"
                                                        disabled={
                                                            !isEntityEnabled(
                                                                rowData
                                                            )
                                                        }
                                                        onClick={() => {
                                                            handleCefCheck(
                                                                isOff(
                                                                    rowData.cef
                                                                ),
                                                                {
                                                                    ...rowData,
                                                                }
                                                            );
                                                        }}
                                                    />
                                                </>
                                            )
                                        }
                                    </RsuiteTable.Cell>
                                </RsuiteTable.Column>

                                {!isEnroll && (
                                    <>
                                        <RsuiteTable.Column
                                            flexGrow={2}
                                            minWidth={200}
                                            fixed
                                        >
                                            <RsuiteTable.HeaderCell
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <>
                                                    <Text>
                                                        {t(
                                                            "tenantConfig.axImportControl.tableHeader.triggerDataTransfer"
                                                        )}

                                                        <Whisper
                                                            placement="topStart"
                                                            trigger="hover"
                                                            speaker={
                                                                <Tooltip>
                                                                    {t(
                                                                        "tenantConfig.axImportControl.dataTransfer.tooltip"
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <HelpOutlineIcon
                                                                style={{
                                                                    marginLeft:
                                                                        "0.25rem",
                                                                    marginBottom:
                                                                        "0.125rem",
                                                                }}
                                                            />
                                                        </Whisper>
                                                    </Text>
                                                </>
                                            </RsuiteTable.HeaderCell>
                                            <RsuiteTable.Cell
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {(rowData: DataImport) =>
                                                    isSupported(
                                                        rowData.cef
                                                    ) && (
                                                        <>
                                                            <Button
                                                                className={cn(
                                                                    "ax-import-entities-table__action-button",
                                                                    "ax-import-entities-table__transfer-all-btn"
                                                                )}
                                                                onClick={() =>
                                                                    onMarkAndTransferAll?.(
                                                                        rowData
                                                                    )
                                                                }
                                                                disabled={
                                                                    !isCefEnabled(
                                                                        rowData
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    "tenantConfig.axImportControl.dataTransfer.buttonTitle"
                                                                )}
                                                            </Button>
                                                        </>
                                                    )
                                                }
                                            </RsuiteTable.Cell>
                                        </RsuiteTable.Column>

                                        <RsuiteTable.Column
                                            flexGrow={2}
                                            minWidth={200}
                                            fixed
                                        >
                                            <RsuiteTable.HeaderCell
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                <>
                                                    <Text>
                                                        {t(
                                                            "tenantConfig.axImportControl.tableHeader.settings"
                                                        )}

                                                        <Whisper
                                                            placement="topStart"
                                                            trigger="hover"
                                                            speaker={
                                                                <Tooltip>
                                                                    {t(
                                                                        "tenantConfig.axImportControl.settings.tooltip"
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <HelpOutlineIcon
                                                                style={{
                                                                    marginLeft:
                                                                        "0.25rem",
                                                                    marginBottom:
                                                                        "0.125rem",
                                                                }}
                                                            />
                                                        </Whisper>
                                                    </Text>
                                                </>
                                            </RsuiteTable.HeaderCell>

                                            <RsuiteTable.Cell
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {(rowData: DataImport) =>
                                                    rowData.hasSettings && (
                                                        <>
                                                            <IconButton
                                                                className="ax-import-entities-table__icon"
                                                                appearance="subtle"
                                                                size={"md"}
                                                                icon={
                                                                    <GearIcon />
                                                                }
                                                                disabled={isDocImportOff(
                                                                    rowData
                                                                )}
                                                                onClick={() => {
                                                                    handleOpenSettings(
                                                                        rowData
                                                                    );
                                                                }}
                                                            />
                                                        </>
                                                    )
                                                }
                                            </RsuiteTable.Cell>
                                        </RsuiteTable.Column>
                                    </>
                                )}
                            </>
                        )}
                    </RsuiteTable>
                </>
            )}
        </>
    );
};

export default Table;
